import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { Composant, ComposantsService } from '../../../services/composants.service';
import { ClasseComposant, ClassesComposantsService } from '../../../services/classes-composants.service';

@Component({
  selector: 'app-voir-composants',
  imports: [CommonModule, FormsModule],
  templateUrl: './voir-composants.component.html',
  styleUrl: './voir-composants.component.scss'
})
export class VoirComposantsComponent implements OnInit, OnDestroy {
  protected composants: Composant[] = [];
  protected filter = '';
  protected classeFilter = '';
  protected classesComposants: ClasseComposant[] = [];
  protected page = 1;
  protected readonly pageSize = 10;
  protected loading = false;
  protected error = '';
  protected success = '';
  protected deletingId: number | null = null;
  protected imageModalUrl: string | null = null;
  protected imageModalAlt = '';

  private loadingTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly apiBaseUrl = environment.apiBaseUrl;


  constructor(
    private readonly composantsService: ComposantsService,
    private readonly classesComposantsService: ClassesComposantsService,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadComposants();
    this.classesComposantsService.list().subscribe({
      next: (items) => {
        this.classesComposants = items;
        this.cdr.detectChanges();
      },
      error: () => {
        this.classesComposants = [];
      }
    });

    // Fallback: if HttpClient path hangs in browser, force a direct fetch path.
    setTimeout(() => {
      if (this.loading) {
        this.loadComposantsWithFetchFallback();
      }
    }, 2500);
  }

  ngOnDestroy(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
    }
  }

  protected get filteredComposants(): Composant[] {
    const q = this.normalizeText(this.filter);
    const selectedClasseName = this.normalizeText(this.classeFilter);

    const base = this.composants.filter((c) => {
      if (!selectedClasseName) {
        return true;
      }
      return this.normalizeText(c.classe_nom) === selectedClasseName;
    });

    if (!q) {
      return base;
    }

    return base.filter((c) => {
      const text = [c.nom, c.classe_nom, c.ref_fabricant, c.standard_emballage, c.commentaire]
        .filter((v): v is string => !!v)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return text.includes(q);
    });
  }

  // La liste des classes est maintenant alimentée par l'API et stockée dans classesComposants

  protected get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredComposants.length / this.pageSize));
  }

  protected get pagedComposants(): Composant[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredComposants.slice(start, start + this.pageSize);
  }

  protected onFilterChange(): void {
    this.page = 1;
  }

  protected onClasseFilterChange(): void {
    this.page = 1;
  }

  protected prevPage(): void {
    this.page = Math.max(1, this.page - 1);
  }

  protected nextPage(): void {
    this.page = Math.min(this.totalPages, this.page + 1);
  }

  protected refresh(): void {
    this.success = '';
    this.classeFilter = '';
    this.loadComposants();
  }

  protected deleteComposant(composant: Composant): void {
    if (!composant?.id) {
      return;
    }

    const confirmed = window.confirm(`Confirmer la suppression du composant "${composant.nom}" ?`);
    if (!confirmed) {
      return;
    }

    this.success = '';
    this.error = '';
    this.deletingId = composant.id;

    this.composantsService.delete(composant.id).subscribe({
      next: () => {
        this.composants = this.composants.filter((item) => item.id !== composant.id);
        this.page = Math.min(this.page, this.totalPages);
        this.success = `Composant supprimé : ${composant.nom}`;
        this.deletingId = null;
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.error = this.extractErrorMessage(err, 'Suppression impossible.');
        this.deletingId = null;
        this.cdr.detectChanges();
      }
    });
  }

  protected editComposant(composant: Composant): void {
    if (!composant?.id) {
      return;
    }

    this.router.navigate(['/intranet/gestion-stocks/composants/modifier'], {
      queryParams: { id: composant.id }
    });
  }

  protected photoUrl(photo: string | null): string {
    if (!photo) {
      return '';
    }

    if (photo.startsWith('data:image/')) {
      return photo;
    }

    return '';
  }

  protected openImageModal(photo: string | null, alt: string): void {
    const url = this.photoUrl(photo);
    if (!url) {
      return;
    }

    this.imageModalUrl = url;
    this.imageModalAlt = alt || 'Image composant';
  }

  protected closeImageModal(): void {
    this.imageModalUrl = null;
    this.imageModalAlt = '';
  }

  @HostListener('document:keydown.escape')
  protected onEscapePressed(): void {
    if (this.imageModalUrl) {
      this.closeImageModal();
    }
  }

  private loadComposants(): void {
    this.loading = true;
    this.success = '';
    this.error = '';
    this.startLoadingFailsafe();

    this.composantsService.list().subscribe({
      next: (items) => {
        this.zone.run(() => {
          this.clearLoadingFailsafe();
          this.loading = false;
          const safeItems = Array.isArray(items) ? items : [];
          this.composants = safeItems;

          try {
            this.page = Math.min(this.page, this.totalPages);
          } catch {
            this.page = 1;
          }

          this.cdr.detectChanges();
        });
      },
      error: (err: unknown) => {
        this.zone.run(() => {
          this.clearLoadingFailsafe();
          this.error = this.extractErrorMessage(err, 'Impossible de charger les composants.');
          this.loading = false;
          this.cdr.detectChanges();
        });
      }
    });
  }

  private async loadComposantsWithFetchFallback(): Promise<void> {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/composants`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = (await response.json()) as { items?: Composant[] } | Composant[];
      const items = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

      this.composants = items;
      this.loading = false;
      this.error = '';
      this.clearLoadingFailsafe();
      this.page = Math.min(this.page, this.totalPages);
      this.cdr.detectChanges();
    } catch {
      // Keep existing error/failsafe message if fallback also fails.
    }
  }

  private startLoadingFailsafe(): void {
    this.clearLoadingFailsafe();
    this.loadingTimer = setTimeout(() => {
      this.zone.run(() => {
        this.loading = false;
        if (!this.error) {
          this.error = 'Le chargement prend trop de temps. Vérifie la connexion puis rafraîchis la page.';
        }
        this.loadingTimer = null;
        this.cdr.detectChanges();
      });
    }, 35000);
  }

  private clearLoadingFailsafe(): void {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
  }

  private extractErrorMessage(err: unknown, fallback: string): string {
    const maybe = err as { error?: { detail?: string }; message?: string };
    return maybe?.error?.detail ?? maybe?.message ?? fallback;
  }

  private normalizeText(value: string | null | undefined): string {
    return (value ?? '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  }
}
